<template>
  <v-data-table :headers="headers" :items="items" :search="search" height="calc(100vh - 340px)" fixed-header
    :items-per-page="50" :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
    class="dashboard-table elevation-1" @current-items="$emit('filtered-items', $event)">
    <!-- Row styling based on alerts -->
    <template v-slot:item="{ item, headers }">
      <tr :class="[getRowClass(item), 'clickable-row']" @click="$emit('open-detail', item)">
        <td v-for="header in headers" :key="header.value" :class="getCellClass(header)">
          <!-- ID Column -->
          <template v-if="header.value === 'id'">
            <span class="font-weight-medium">{{ item.id }}</span>
          </template>

          <!-- Status indicators column -->
          <template v-else-if="header.value === 'statusIndicators'">
            <div class="d-flex align-center justify-center flex-wrap" style="gap: 4px;">
              <v-tooltip v-for="alert in getActiveAlerts(item)" :key="`${item.id}-${alert.key}`" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon :color="alert.color" small v-bind="attrs" v-on="on">
                    {{ alert.icon }}
                  </v-icon>
                </template>
                <span>{{ alert.tooltip }}</span>
              </v-tooltip>
              <span v-if="!hasAnyAlert(item)" class="grey--text text-caption">—</span>
            </div>
          </template>

          <!-- Name column -->
          <template v-else-if="header.value === 'name'">
            <div class="d-flex align-center">
              <span class="text-truncate" style="max-width: 280px">{{ item.name }}</span>
              <v-chip v-if="item.openedTicket" x-small color="warning" class="ml-2">
                <v-icon x-small left>mdi-ticket</v-icon>
                Ticket
              </v-chip>
            </div>
          </template>

          <!-- Date columns with color coding -->
          <template v-else-if="header.value === 'lastPing'">
            <v-chip :color="getDateChipColor(item.lastPing, 3)" x-small label>
              {{ formatDate(item.lastPing) }}
            </v-chip>
          </template>

          <template v-else-if="header.value === 'terminalLastPing'">
            <div class="d-flex flex-column align-start" style="gap: 2px; line-height: 1.15">
              <v-chip :color="getDateChipColor(item.terminalLastPing, 3)" x-small label>
                {{ formatDate(item.terminalLastPing) }}
              </v-chip>
              <span v-if="item.tpaSerialNumber && item.tpaSerialNumber !== 'N/A'" class="text-truncate" style="font-family: monospace; font-size: 12px; letter-spacing: .4px;
         max-width: 135px; display: inline-block; line-height: 1.5;
         padding: 0 6px; border-radius: 4px;
         background: rgba(96,125,169,.32); border: 1px solid rgba(96,125,169,.5);" :title="item.tpaSerialNumber">
                {{ item.tpaSerialNumber }}
              </span>
              <span v-else style="opacity: .3; font-size: 12px">—</span>
            </div>
          </template>

          <template v-else-if="header.value === 'lastSale'">
            <v-chip :color="getDateChipColor(item.lastSale, 3)" x-small label>
              {{ formatDate(item.lastSale) }}
            </v-chip>
          </template>

          <!-- Voltage with color coding -->
          <template v-else-if="header.value === 'batteryVoltage'">
            <v-chip :color="getVoltageColor(item.batteryVoltage)" x-small label>
              {{ formatVoltage(item.batteryVoltage) }} V
            </v-chip>
          </template>

          <template v-else-if="header.value === 'solarPanelVoltage'">
            <span>{{ formatVoltage(item.solarPanelVoltage) }} V</span>
          </template>

          <!-- Validated sales with color coding -->
          <template v-else-if="header.value === 'validatedSales'">
            <v-chip :color="item.validatedSales >= 70 ? 'success' : 'warning'" x-small label>
              {{ item.validatedSales }}%
            </v-chip>
          </template>

          <!-- Cancelled sales with color coding -->
          <template v-else-if="header.value === 'cancelledSales'">
            <v-chip :color="item.cancelledSales > 30 ? 'error' : 'success'" x-small label>
              {{ item.cancelledSales }}%
            </v-chip>
          </template>

          <!-- Opened ticket column -->
          <template v-else-if="header.value === 'openedTicket'">
            <v-icon :color="item.openedTicket ? 'warning' : 'success'" small>
              {{ item.openedTicket ? 'mdi-ticket' : 'mdi-ticket-outline' }}
            </v-icon>
          </template>

          <!-- Default -->
          <template v-else>
            {{ item[header.value] }}
          </template>
        </td>
      </tr>
    </template>
  </v-data-table>
</template>

<script>
export default {
  name: 'DashboardTable',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    search: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      headers: [
        { text: 'ID', value: 'id', width: '80px' },
        { text: 'Alertes', value: 'statusIndicators', width: '180px', align: 'center', sortable: false },
        { text: 'Nom', value: 'name', width: '300px' },
        { text: 'Dernier ping', value: 'lastPing', width: '140px' },
        { text: 'TPA (ping / série)', value: 'terminalLastPing', width: '150px' },
        { text: 'Dernière vente', value: 'lastSale', width: '140px' },
        { text: 'Batterie', value: 'batteryVoltage', width: '90px' },
        { text: 'Solaire', value: 'solarPanelVoltage', width: '80px' },
        { text: 'Ventes', value: 'salesCount', width: '70px' },
        { text: 'Valid.', value: 'validatedSales', width: '70px' },
        { text: 'Annul.', value: 'cancelledSales', width: '70px' },
        { text: 'Stock', value: 'fullBottleCount', width: '60px' },
        { text: 'Err.', value: 'errorLockerCount', width: '50px' },
        { text: 'Ticket', value: 'openedTicket', width: '60px', align: 'center' }
      ],
      alertIndicators: [
        { key: 'salesAlert', color: 'purple', icon: 'mdi-cart-off', tooltip: 'Alerte vente' },
        { key: 'tpaAlert', color: 'green', icon: 'mdi-credit-card-off', tooltip: 'Alerte TPA' },
        { key: 'pingAlert', color: 'blue', icon: 'mdi-wifi-off', tooltip: 'Alerte ping' },
        { key: 'cancelledSalesAlert', color: 'yellow darken-2', icon: 'mdi-cart-remove', tooltip: 'Ventes annulées' },
        { key: 'rackAlert', color: 'orange', icon: 'mdi-server-off', tooltip: 'Com rack KO' },
        { key: 'voltageAlert', color: 'red', icon: 'mdi-flash-alert', tooltip: 'Tension faible' },
        { key: 'motherboardFreezeAlert', color: 'cyan', icon: 'mdi-snowflake-alert', tooltip: 'Carte mère freeze' },
        { key: 'fuseAlert', color: 'pink', icon: 'mdi-fuse-alert', tooltip: 'Fusible' },
        { key: 'batteryAlert', color: 'deep-orange', icon: 'mdi-battery-alert', tooltip: 'Batterie' }
      ]
    };
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr || dateStr === 'N/A') return 'N/A';
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    },
    getActiveAlerts(item) {
      return this.alertIndicators.filter(alert => item[alert.key] === true);
    },
    hasAnyAlert(item) {
      return this.alertIndicators.some(alert => item[alert.key]);
    },
    getRowClass(item) {
      const alertCount = this.alertIndicators.filter(alert => item[alert.key]).length;
      if (alertCount >= 3) return 'row-critical';
      if (alertCount >= 1) return 'row-warning';
      return '';
    },
    getCellClass(header) {
      return header.align === 'center' ? 'text-center' : '';
    },
    getDateChipColor(dateStr, daysThreshold) {
      if (!dateStr || dateStr === 'N/A') return 'grey';
      const date = new Date(dateStr);
      const now = new Date();
      const diffDays = (now - date) / (1000 * 60 * 60 * 24);
      if (diffDays > daysThreshold) return 'error';
      if (diffDays > 1) return 'warning';
      return 'success';
    },
    getVoltageColor(voltage) {
      if (!voltage || voltage === 'N/A') return 'grey';
      const v = parseFloat(voltage);
      if (v < 11.7) return 'error';
      if (v < 12) return 'warning';
      return 'success';
    },
    formatVoltage(v) {
      if (v === null || v === undefined || v === 'N/A') return v;
      const n = Number(v);
      return isNaN(n) ? v : Math.round(n * 100) / 100;
    }
  }
};
</script>

<style scoped>
.dashboard-table {
  font-size: 14px;
}

.dashboard-table>>>.v-data-table__wrapper {
  overflow-x: auto;
}

.dashboard-table>>>th {
  white-space: nowrap;
  font-size: 13px !important;
  font-weight: 600 !important;
  background-color: #f5f5f5 !important;
  padding: 12px 16px !important;
}

.dashboard-table>>>td {
  font-size: 14px !important;
  padding: 14px 16px !important;
  height: 56px !important;
}

.dashboard-table>>>.v-chip {
  font-size: 12px !important;
  height: 24px !important;
}

.dashboard-table>>>.v-icon {
  font-size: 18px !important;
}

.row-critical {
  background-color: rgba(244, 67, 54, 0.08) !important;
}

.row-warning {
  background-color: rgba(255, 152, 0, 0.05) !important;
}

.row-critical:hover,
.row-warning:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

/* Clickable rows */
.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.clickable-row:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.row-critical.clickable-row:hover {
  background-color: rgba(244, 67, 54, 0.15) !important;
}

.row-warning.clickable-row:hover {
  background-color: rgba(255, 152, 0, 0.12) !important;
}
</style>
